import { useEffect, useRef, useState } from 'react';

/**
 * A date field that ALWAYS displays `MM/DD/YYYY` (standing rule 3), whatever the
 * viewer's browser locale is.
 *
 * Why this exists: a native `<input type="date">` renders its text in the
 * BROWSER's locale and that cannot be overridden — not by CSS, not by the `lang`
 * attribute (verified in Chrome 2026-09-24: `lang="en-GB"` / `lang="de-DE"` render
 * identically to no lang at all). So a user whose Chrome is set to a non-US locale
 * saw `21-12-2024` where the lab expects `12/21/2024`.
 *
 * So the TEXT is ours — a plain text input formatted and parsed as MM/DD/YYYY —
 * while the CALENDAR is still the browser's: a visually-hidden native date input
 * is kept alongside and opened with `showPicker()` from the calendar button, so
 * users keep the native picker they are used to.
 *
 * The value crossing `onChange` is always the ISO `yyyy-mm-dd` the form stores,
 * so this is a drop-in for the native input it replaces.
 */

/** `yyyy-mm-dd` → `MM/DD/YYYY` ('' when absent or unparseable). */
export function isoToUs(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/** `MM/DD/YYYY` (also `M/D/YY`) → `yyyy-mm-dd`; '' when it isn't a real date. */
export function usToIso(text: string | null | undefined): string {
  if (!text) return '';
  const m = /^\s*(\d{1,2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{2}|\d{4})\s*$/.exec(text);
  if (!m) return '';
  const [, mRaw, dRaw, yRaw] = m as unknown as [string, string, string, string];
  const mm = Number(mRaw);
  const dd = Number(dRaw);
  let yy = Number(yRaw);
  if (yRaw.length === 2) yy += yy < 50 ? 2000 : 1900;
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return '';
  // Round-trip through Date so 02/31 is rejected rather than silently rolled over.
  const d = new Date(yy, mm - 1, dd);
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${yy}-${p(mm)}-${p(dd)}`;
}

export function UsDateInput({ id, className, value, readOnly, min, onChange }: {
  id?: string;
  className?: string;
  /** ISO `yyyy-mm-dd`, or '' / null when empty. */
  value: string | null | undefined;
  readOnly?: boolean;
  /** Optional earliest selectable date, ISO `yyyy-mm-dd`. The native calendar
   *  disables anything before it, and a typed earlier date is rejected on blur. */
  min?: string;
  /** Receives ISO `yyyy-mm-dd`, or '' when cleared. */
  onChange: (iso: string) => void;
}) {
  const [text, setText] = useState(() => isoToUs(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  // Track the value when it changes upstream (record switch, Cancel, Save & New)
  // — but never while the user is mid-edit with an as-yet-unparseable string.
  useEffect(() => { setText(isoToUs(value)); }, [value]);

  /** Commit on blur: a valid date is normalised, anything else reverts to the
   *  last good value rather than silently wiping it. */
  const commit = () => {
    const raw = text.trim();
    if (raw === '') { setText(''); onChange(''); return; }
    const iso = usToIso(raw);
    // Reject a date earlier than `min` (typed path — the calendar already blocks
    // it): revert to the last good value rather than accept an out-of-range date.
    if (iso && (!min || iso >= min)) { setText(isoToUs(iso)); onChange(iso); }
    else setText(isoToUs(value));
  };

  return (
    <span className="usdate">
      <input
        id={id}
        className={className}
        type="text"
        inputMode="numeric"
        // No placeholder: an empty date renders as a PLAIN EMPTY BOX, as legacy
        // does (user request 2026-09-24 — "when there is a null, don't display the
        // format"). The expected format still reaches screen readers through the
        // aria-label, and a typed value is normalised to MM/DD/YYYY on blur.
        aria-label="Date, MM/DD/YYYY"
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      />
      {!readOnly && (
        <>
          <button
            type="button"
            className="usdate-btn"
            aria-label="Open date picker"
            title="Open date picker"
            onClick={() => {
              const el = pickerRef.current;
              if (!el) return;
              // showPicker() is the supported way to open the native calendar
              // without showing the browser-formatted text; older engines fall
              // back to focus+click, and typing always works regardless.
              try { (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); }
              catch { el.focus(); }
            }}
          >
            <i className="ti ti-calendar" />
          </button>
          {/* The calendar itself — present for the picker, never shown, and kept
              out of the tab order so keyboard users get the text field. */}
          <input
            ref={pickerRef}
            className="usdate-native"
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            min={min}
            value={value ?? ''}
            onChange={(e) => {
              // Guard the picker path too: ignore a value below `min`.
              if (min && e.target.value && e.target.value < min) return;
              setText(isoToUs(e.target.value)); onChange(e.target.value);
            }}
          />
        </>
      )}
    </span>
  );
}
